import { useState } from 'react';

function toISO(date) {
  return date.toISOString().slice(0, 10);
}

function resolveShortcut(shortcut) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  switch (shortcut) {
    case 'thisMonth': {
      return { from: toISO(new Date(y, m, 1)), to: toISO(new Date(y, m + 1, 1)) };
    }
    case 'lastMonth': {
      return { from: toISO(new Date(y, m - 1, 1)), to: toISO(new Date(y, m, 1)) };
    }
    case 'thisYear': {
      return { from: toISO(new Date(y, 0, 1)), to: toISO(new Date(y + 1, 0, 1)) };
    }
    case 'last12months': {
      const to = new Date(y, m + 1, 1);
      const from = new Date(y - 1, m + 1, 1);
      return { from: toISO(from), to: toISO(to) };
    }
    default:
      return null;
  }
}

function exclusiveTo(inclusiveTo) {
  if (!inclusiveTo) return '';
  const d = new Date(inclusiveTo + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  return toISO(d);
}

export { resolveShortcut };

const SHORTCUTS = [
  { key: 'thisMonth',    label: 'Ce mois' },
  { key: 'lastMonth',   label: 'Mois dernier' },
  { key: 'thisYear',    label: 'Cette année' },
  { key: 'last12months', label: '12 derniers mois' },
  { key: 'custom',      label: 'Personnalisé' },
];

/**
 * PeriodSelector — pills scrollables + dates custom.
 * onChange({ from, to }) — from incluse, to exclusive (bornes API).
 * colorScheme : 'blue' | 'amber'
 */
export default function PeriodSelector({ onChange, colorScheme = 'blue', label, initialShortcut = 'thisMonth' }) {
  const [shortcut, setShortcut] = useState(initialShortcut);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  function handleShortcut(key) {
    setShortcut(key);
    if (key !== 'custom') {
      const resolved = resolveShortcut(key);
      if (resolved) onChange(resolved);
    }
  }

  function handleCustomChange(from, to) {
    if (from && to && from <= to) {
      onChange({ from, to: exclusiveTo(to) });
    }
  }

  const activePill =
    colorScheme === 'amber'
      ? 'bg-amber-600 text-white shadow-sm'
      : 'bg-blue-600 text-white shadow-sm';
  const inactivePill =
    colorScheme === 'amber'
      ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent';
  const customBorder =
    colorScheme === 'amber'
      ? 'border-amber-300 focus:ring-amber-400'
      : 'border-slate-300 focus:ring-blue-400';

  return (
    <div className="space-y-2.5">
      {label && (
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">{label}</p>
      )}
      <div className="flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1 scrollbar-none">
        {SHORTCUTS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => handleShortcut(s.key)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all whitespace-nowrap shrink-0 ${
              shortcut === s.key ? activePill : inactivePill
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {shortcut === 'custom' && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => {
              setCustomFrom(e.target.value);
              handleCustomChange(e.target.value, customTo);
            }}
            className={`flex-1 min-w-[130px] text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 ${customBorder}`}
            aria-label="Date de début"
          />
          <span className="text-xs text-slate-400 shrink-0">→</span>
          <input
            type="date"
            value={customTo}
            min={customFrom}
            onChange={(e) => {
              setCustomTo(e.target.value);
              handleCustomChange(customFrom, e.target.value);
            }}
            className={`flex-1 min-w-[130px] text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 ${customBorder}`}
            aria-label="Date de fin"
          />
        </div>
      )}
    </div>
  );
}
